import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCallback } from "react";

const WAIT_TIMEOUT_MS = 4000;
const POLL_MS = 100;

/**
 * useCaptcha returns an async function that resolves to a reCAPTCHA v3 token
 * for the given action, or null if the v3 script never initializes
 * (e.g. when the site key isn't authorized for this domain — common in preview
 * environments). The submit handler decides what to do with null.
 */
export function useCaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useCallback(
    async (action) => {
      // Wait up to WAIT_TIMEOUT_MS for executeRecaptcha to become available.
      const start = Date.now();
      let fn = executeRecaptcha;
      while (!fn && Date.now() - start < WAIT_TIMEOUT_MS) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, POLL_MS));
        // The hook value is captured at render time; check window.grecaptcha as
        // a fallback signal that the script has loaded for the current domain.
        if (typeof window !== "undefined" && window.grecaptcha?.execute) {
          fn = executeRecaptcha;
          if (!fn) {
            // hook callback still missing — bail and return null
            break;
          }
        }
      }

      if (!fn) return null;
      try {
        return await fn(action);
      } catch (e) {
        // reCAPTCHA execution failure is non-fatal; null tells the caller to skip.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("reCAPTCHA execute error:", e?.message || e);
        }
        return null;
      }
    },
    [executeRecaptcha]
  );
}
